import { ReactNode, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { cn } from '../lib/utils'
import { useAuth } from '../hooks/useAuth'
import { useCompany } from '../hooks/useCompany'
import { RoleBasedComponent } from './RoleBasedComponent'
import { CompanyModal } from './CompanyModal'

interface NavigationItem {
  name: string
  href: string
  icon: ReactNode
  roles: string[]
  subItems?: NavigationItem[]
}

const navigation: NavigationItem[] = [
  {
    name: 'Time',
    href: '/time',
    roles: ['OWNER', 'ADMIN', 'MEMBER'],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    subItems: [
      { name: 'Clock', href: '/time/clock', roles: ['OWNER', 'ADMIN', 'MEMBER'], icon: null },
      { name: 'Schedule', href: '/time/schedule', roles: ['OWNER', 'ADMIN', 'MEMBER'], icon: null },
      { name: 'Timecards', href: '/time/timecards', roles: ['OWNER', 'ADMIN', 'MEMBER'], icon: null },
      { name: 'PTO', href: '/time/pto', roles: ['OWNER', 'ADMIN', 'MEMBER'], icon: null },
      { name: 'Availability', href: '/time/availability', roles: ['OWNER', 'ADMIN', 'MEMBER'], icon: null },
      { name: 'Time Settings', href: '/time/settings', roles: ['OWNER', 'ADMIN'], icon: null },
    ]
  },
  {
    name: 'Dashboard',
    href: '/dashboard',
    roles: ['OWNER', 'ADMIN', 'MEMBER'],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v14l-5-3-5 3V5z" />
      </svg>
    )
  },
  {
    name: 'Locations',
    href: '/locations',
    roles: ['OWNER', 'ADMIN'],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    )
  },
  {
    name: 'People',
    href: '/people',
    roles: ['OWNER', 'ADMIN'],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
      </svg>
    )
  },
  {
    name: 'Teams',
    href: '/teams',
    roles: ['OWNER', 'ADMIN'],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    )
  },
  {
    name: 'Reports',
    href: '/reports',
    roles: ['OWNER', 'ADMIN'],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    )
  },
  {
    name: 'Settings',
    href: '/settings',
    roles: ['OWNER', 'ADMIN'],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    )
  },
  {
    name: 'Help',
    href: '/help',
    roles: ['OWNER', 'ADMIN', 'MEMBER'],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  },
]

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
  isCollapsed?: boolean
  onToggleCollapse?: () => void
}

export function Sidebar({ isOpen, onClose, isCollapsed = false, onToggleCollapse }: SidebarProps) {
  const location = useLocation()
  const { user } = useAuth()
  const { currentCompany, availableCompanies, switchCompany, joinCompany, createCompany } = useCompany()
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false)
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false)

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === href
    }
    return location.pathname.startsWith(href)
  }

  const isTimeSection = location.pathname.startsWith('/time')

  const handleCompanySwitch = (companyId: string) => {
    switchCompany(companyId)
    setIsCompanyDropdownOpen(false)
  }

  const handleJoinCompany = async (companyCode: string) => {
    try {
      await joinCompany(companyCode)
    } catch (error) {
      console.error('Failed to join company:', error)
      // In a real app, you'd show a proper error message
    }
  }

  const handleCreateCompany = async (companyName: string) => {
    try {
      await createCompany(companyName)
    } catch (error) {
      console.error('Failed to create company:', error)
      // In a real app, you'd show a proper error message
    }
  }

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div className={cn(
        'fixed inset-y-0 left-0 z-50 bg-white shadow-lg transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0',
        isCollapsed ? 'w-16' : 'w-64',
        isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      )}>
        {/* Brand area */}
        <div className={cn(
          "flex items-center h-16 border-b border-gray-200",
          isCollapsed ? "justify-center px-4" : "justify-between px-6"
        )}>
          {!isCollapsed && (
            <div className="flex-1 min-w-0 relative">
              <button
                onClick={() => setIsCompanyDropdownOpen(!isCompanyDropdownOpen)}
                className="flex items-center w-full text-left hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <h1 className="text-lg font-semibold text-gray-900">Clockin</h1>
                  <p className="text-sm text-gray-500 truncate">
                    {currentCompany?.name || user?.teamName || (availableCompanies.length === 0 ? 'No Company' : 'Select Company')}
                  </p>
                </div>
                <svg 
                  className={cn(
                    "w-4 h-4 text-gray-400 ml-2 transition-transform",
                    isCompanyDropdownOpen ? "transform rotate-180" : ""
                  )} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Company Dropdown */}
              {isCompanyDropdownOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setIsCompanyDropdownOpen(false)}
                  />
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
                    <div className="py-1">
                      {availableCompanies.map((company) => (
                        <button
                          key={company.id}
                          onClick={() => handleCompanySwitch(company.id)}
                          className={cn(
                            "w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors",
                            currentCompany?.id === company.id ? "bg-blue-50 text-blue-700" : "text-gray-900"
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{company.name}</p>
                              <p className="text-sm text-gray-500 capitalize">{company.user_role.toLowerCase()}</p>
                            </div>
                            {currentCompany?.id === company.id && (
                              <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </button>
                      ))}
                      <div className="border-t border-gray-200">
                        <button
                          onClick={() => {
                            setIsCompanyDropdownOpen(false)
                            setIsCompanyModalOpen(true)
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-gray-900 flex items-center"
                        >
                          <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          <span className="font-medium">Join or Create Company</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          {isCollapsed && (
            <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">
                {currentCompany?.name?.[0] || 'W'}
              </span>
            </div>
          )}
          <button
            onClick={onClose}
            className="lg:hidden p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Collapse/Expand toggle button - Desktop only */}
        <div className="hidden lg:block border-t border-gray-200 p-4">
          <button
            onClick={onToggleCollapse}
            className={cn(
              "w-full flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors duration-200",
              isCollapsed ? "px-2 py-3 justify-center" : "px-3 py-2"
            )}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <svg className={cn("w-5 h-5", isCollapsed ? "" : "mr-3")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isCollapsed ? "M13 5l7 7-7 7" : "M11 19l-7-7 7-7"} />
            </svg>
            {!isCollapsed && "Collapse"}
          </button>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
          {navigation.map((item) => (
            <RoleBasedComponent key={item.name} allowedRoles={item.roles as any[]}>
              <div>
                <Link
                  to={item.href}
                  onClick={() => onClose()}
                  className={cn(
                    'flex items-center text-sm font-medium rounded-lg transition-colors duration-200',
                    isCollapsed ? 'px-2 py-3 justify-center' : 'px-3 py-2',
                    isActive(item.href)
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  )}
                  title={isCollapsed ? item.name : undefined}
                >
                  <span className={cn(isCollapsed ? '' : 'mr-3 rtl:ml-3 rtl:mr-0')}>
                    {item.icon}
                  </span>
                  {!isCollapsed && item.name}
                </Link>
                
                {/* Sub-navigation for Time section - only show when not collapsed */}
                {!isCollapsed && item.name === 'Time' && isTimeSection && item.subItems && (
                  <div className="ml-6 mt-1 space-y-1 rtl:ml-0 rtl:mr-6">
                    {item.subItems.map((subItem) => (
                      <RoleBasedComponent key={subItem.name} allowedRoles={subItem.roles as any[]}>
                        <Link
                          to={subItem.href}
                          onClick={() => onClose()}
                          className={cn(
                            'block px-3 py-2 text-sm rounded-lg transition-colors duration-200',
                            location.pathname === subItem.href
                              ? 'bg-gray-100 text-gray-900 font-medium'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          )}
                        >
                          {subItem.name}
                        </Link>
                      </RoleBasedComponent>
                    ))}
                  </div>
                )}
              </div>
            </RoleBasedComponent>
          ))}
        </nav>
      </div>

      {/* Company Modal */}
      <CompanyModal
        isOpen={isCompanyModalOpen}
        onClose={() => setIsCompanyModalOpen(false)}
        onJoinCompany={handleJoinCompany}
        onCreateCompany={handleCreateCompany}
      />
    </>
  )
}